import React, {useCallback, useMemo, useState} from "react";
import styled, {css} from "styled-components";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCog} from "@fortawesome/free-solid-svg-icons";

// Utils
import {exists, getAlpha, getNumeric} from "../../utils/helpers.js";

// Style
import {border, pad, transition} from "../../style/components/variables.js";
import {Text, scrollbar} from "../../style/components/general.js";
import {z} from "../../style/components/mixins.js";

const RenderReportExcel = ({targetWorkbook, sections, targetDirection, handleShowOptions}) => {
  const [colLength, setColLength] = useState(8);
  const [rowLength, setRowLength] = useState(20);

  const getCodeIndices = code => {
    // Split Code based on transition from alpha to numeric
    const [col, row] = code.split(/(?<=[a-zA-Z])(?=\d)/);
    return [getNumeric(col), Number(row) - 1]; // conver letter offset row
  };

  const getMergedCellSpan = (merges, colIndex, rowIndex) => {
    let offset = {rowSpan: 1, colSpan: 1};
    merges?.map(({s, e}) => {
      if (rowIndex >= s.r && rowIndex <= e.r && colIndex >= s.c && colIndex <= e.c) {
        const rowSpan = e.r - s.r + 1;
        const colSpan = e.c - s.c + 1;
        offset = {
          rowSpan: rowIndex === s.r ? rowSpan : 0,
          colSpan: colIndex === s.c ? colSpan : 0
        };
      }
    });
    return offset;
  };

  const sectionMap = useMemo(() => {
    const map = {};
    sections?.map(({targetSheet, headers, data, mappings}) => {
      const sheetData = targetWorkbook.Sheets[targetSheet];
      const [min, max] = headers;
      const [minCol, minRow] = getCodeIndices(min);
      const [maxCol, maxRow] = getCodeIndices(max);

      const dataLengthRow = targetDirection === "Row" ? data?.length || 0 : 0;
      const dataLengthColumn = targetDirection === "Column" ? data?.length || 0 : 0;

      // Update table preview size
      if (maxRow + dataLengthRow >= rowLength) setRowLength(maxRow + 1 + (data?.length || 0));
      if (maxCol + dataLengthColumn >= colLength) setColLength(maxCol + 1 + (data?.length || 0));

      // Populate map with header values
      for (let i = minRow; i < maxRow + 1; i++) {
        for (let j = minCol; j < maxCol + 1; j++) {
          const targetKey = `${getAlpha(j)}${i + 1}`;
          const {colSpan, rowSpan} = getMergedCellSpan(sheetData["!merges"], j, i);
          map[targetKey] = {
            value: sheetData[targetKey]?.v,
            colSpan: colSpan + j <= maxCol ? colSpan : maxCol - j + 1,
            rowSpan: rowSpan + i <= maxRow ? rowSpan : maxRow - i + 1
          };
        }
      }

      // Populate map with cell values
      // TODO: column based chart is receiving wrong startColumn && startRow from the api
      data?.map((cell, dataIdx) =>
        mappings?.map(({startColumn, startRow, cellGroupKey}) => {
          // Build cells out in the direction based on whether start row or column is defined
          const col = Number(startColumn) - 1 + (targetDirection === "Column" ? dataIdx : 0);
          const row = Number(startRow) - 1 + (targetDirection === "Row" ? dataIdx : 0);
          map[`${getAlpha(col)}${row + 1}`] = {value: cell[cellGroupKey]};
        })
      );
    });

    return map;
  }, [sections, targetWorkbook, rowLength, colLength, targetDirection]);

  const controlMap = useMemo(() => {
    const map = {};
    sections?.map(({headers, targetSheet}, i) => {
      const sheetData = targetWorkbook.Sheets[targetSheet];

      const [min, max] = headers;
      const [minCol, minRow] = getCodeIndices(min);
      const [maxCol] = getCodeIndices(max);

      let col = exists(maxCol) ? Number(maxCol) : null;
      const row = exists(minRow) ? Number(minRow) : null;

      for (let j = minCol; j < maxCol + 1; j++) {
        const {colSpan} = getMergedCellSpan(sheetData["!merges"], j, i);
        if (j + colSpan - 1 >= col) {
          col = j;
          break;
        }
      }

      if (col !== null && row !== null)
        map[`${getAlpha(col)}${row + 1}`] = (
          <Option>
            <FontAwesomeIcon icon={faCog} onClick={() => handleShowOptions(i)} />
          </Option>
        );
    });

    return map;
  }, [sections, targetWorkbook, handleShowOptions]);

  const getCells = useCallback(
    (rowIdx, rowSpans) => {
      const temp = [];
      for (let i = 0; i < colLength; i++) {
        const cellKey = `${getAlpha(i)}${rowIdx + 1}`;
        const targetCell = sectionMap[cellKey];
        const {value, colSpan, rowSpan} = targetCell ?? {};

        if (value)
          temp.push(
            <Cell key={i} colSpan={colSpan} rowSpan={rowSpan}>
              {value}
              {controlMap[cellKey]}
            </Cell>
          );

        if (!value && !rowSpans[i]) temp.push(<EmptyCell key={i}>{controlMap[cellKey]}</EmptyCell>);

        if (rowSpan > 1) rowSpans[i] = rowSpan - 1;
        else if (rowSpans[i]) rowSpans[i]--;

        if (colSpan > 1) i += colSpan - 1;
      }
      return temp;
    },
    [colLength, controlMap, sectionMap]
  );

  const getColumns = useCallback(() => {
    const temp = [];

    const rowSpans = Array.from({length: colLength});

    for (let i = 0; i < rowLength; i++) {
      temp.push(
        <Row key={i}>
          <HeaderCell>
            <Text bold>{i + 1}</Text>
          </HeaderCell>
          {getCells(i, rowSpans)}
        </Row>
      );
    }
    return temp;
  }, [colLength, getCells, rowLength]);

  return (
    <ScrollWrapper>
      <table>
        <thead>
          <Row>
            <HeaderCell colSpan={1} />
            {Array.from({length: colLength}, (_, i) => i).map(rowIdx => (
              <HeaderCell key={rowIdx}>
                <Text bold>&nbsp;{getAlpha(rowIdx)}</Text>
              </HeaderCell>
            ))}
          </Row>
        </thead>
        <tbody>{getColumns()}</tbody>
      </table>
    </ScrollWrapper>
  );
};

RenderReportExcel.propTypes = {
  targetWorkbook: PropTypes.objectOf(PropTypes.any),
  sections: PropTypes.arrayOf(PropTypes.any),
  targetDirection: PropTypes.string,
  handleShowOptions: PropTypes.func.isRequired
};

// Style Overrides
const ScrollWrapper = styled.div`
  width: 100%;
  overflow-x: scroll;
  ${scrollbar};

  table {
    width: 100%;
  }
`;

const HeaderCell = styled.th`
  padding: ${pad / 2}px ${pad}px ${pad / 2}px 0;

  p {
    text-align: center;
  }
`;

const Row = styled.tr`
  width: 100%;
`;

const Option = styled.div`
  visibility: hidden;
  opacity: 0;
  z-index: ${z("above")};
  position: absolute;
  transition: ${transition};

  top: 9px;
  right: 5px;

  svg {
    fill: ${props => props.theme.primary};
    cursor: pointer;
  }
`;

const Cell = styled.td`
  text-align: center;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  position: relative;

  ${({active}) =>
    active &&
    css`
      background: blue;
    `};

  &:hover {
    ${Option} {
      visibility: visible;
      opacity: 1;
    }
  }
`;

const EmptyCell = styled(Cell)`
  min-width: 100px;
`;

export default RenderReportExcel;
